import { brand } from '../../config/brand';
import { PlatformIcon } from '../profile/links';

export interface Simulcast {
  platform: string;
  label: string;
  url: string;
}

/**
 * "Watch on Twitch" — where else a broadcast is going out (§6.8).
 *
 * ## One component, because it appears in four places
 *
 * The profile's live section, the on-air modal, the dashboard rail, and the
 * console's own preview of what listeners see. Four copies of an outbound link
 * would eventually be four different opinions about `rel`, and the one that
 * forgot `noopener` is the one that ships.
 *
 * ## It never renders without a destination
 *
 * The server resolves the URL from the host's own profile link and returns null
 * when that link is gone, so `simulcast` being present is the same statement as
 * "this goes somewhere". Nothing here has to guess.
 *
 * ## Sometimes it must not be a link at all
 *
 * The dashboard rail and the on-air modal draw whole rows that are themselves
 * links, and an anchor inside an anchor is invalid HTML that browsers silently
 * un-nest - producing a stray link outside the row and a dead area inside it.
 * `interactive: false` renders the same thing as plain text for those, and the
 * row's own destination carries the click.
 */
export function SimulcastLink({
  simulcast,
  size = 'md',
  interactive = true,
}: {
  simulcast: Simulcast | null;
  /** `sm` for list rows, where it is one fact among several. */
  size?: 'sm' | 'md';
  /** False inside something that is already a link. See above. */
  interactive?: boolean;
}) {
  if (!simulcast) return null;

  const small = size === 'sm';

  if (!interactive) {
    return (
      <span
        data-simulcast-tag={simulcast.platform}
        className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-hot-text"
      >
        <PlatformIcon platform={simulcast.platform} className="h-3 w-3" />
        {simulcast.label}
      </span>
    );
  }

  return (
    <a
      href={simulcast.url}
      target="_blank"
      /*
       * `noopener` denies the opened page a handle on this one. `nofollow`
       * because a host chose this destination, not us. The server pinned the
       * URL to the platform's own hosts, so this cannot point anywhere the
       * label does not claim.
       */
      rel="noopener noreferrer nofollow"
      data-simulcast-link={simulcast.platform}
      title={`Watch this set live on ${simulcast.label}`}
      className={
        small
          ? 'inline-flex items-center gap-1 font-mono text-[10px] text-hot-text hover:underline'
          : 'inline-flex items-center gap-2 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-3.5 py-2 text-[13px] font-medium text-hot-text transition-opacity hover:opacity-85'
      }
      onClick={(event) => event.stopPropagation()}
    >
      <PlatformIcon platform={simulcast.platform} className={small ? 'h-3 w-3' : 'h-4 w-4'} />
      {small ? simulcast.label : `Watch on ${simulcast.label}`}
    </a>
  );
}

/**
 * Where a live broadcast is actually happening, as one icon.
 *
 * ## Why the on-platform case needs a mark of its own
 *
 * `SimulcastLink` renders nothing when a host has not named a platform, which
 * is correct for a "Watch on Twitch" button and wrong in a list. A row with a
 * Twitch glyph beside a row with nothing reads as "Twitch" against "unknown",
 * when the truth is "Twitch" against "here" - and "here" is the more common
 * answer and the one the whole product is about. So every row gets a mark, and
 * the absence of a platform is stated rather than left blank.
 *
 * ## Both cases are named, which the first version got wrong
 *
 * The home mark shipped as an icon on its own, on the reasoning that most rooms
 * are here and a list repeating the brand down its right-hand side is noise. On
 * screen it read as a fault: a tiny unlabelled square at the end of a line of
 * text looks like something that failed to load, not like a statement. Beside a
 * badge that says "Twitch" in words, it also made the two look like different
 * kinds of thing when they are the same fact with two answers.
 *
 * So both are icon plus name. The platform keeps the accent colour because it
 * is the exception worth noticing; here is drawn in the same muted tone as the
 * rest of the line, present without competing.
 *
 * ## The mark is the asset, not a redrawn copy
 *
 * `brand.logoMarkMini`, the three-bar version, because the full mark packs five
 * bars into twelve pixels and turns to mud. Redrawing either inline to inherit
 * `currentColor` would look tidier next to the monochrome platform glyphs and
 * would quietly survive a rebrand that changed everything else, which is the
 * failure the brand config exists to prevent.
 */
export function BroadcastOrigin({
  simulcast,
  size = 'sm',
  interactive = false,
}: {
  simulcast: Simulcast | null;
  size?: 'sm' | 'md';
  /** True only where the row is not itself a link - see `SimulcastLink`. */
  interactive?: boolean;
}) {
  const small = size === 'sm';

  if (simulcast) {
    return (
      <span className={interactive ? 'pointer-events-auto inline-flex' : 'inline-flex'}>
        <SimulcastLink simulcast={simulcast} size={size} interactive={interactive} />
      </span>
    );
  }

  return (
    <span
      data-broadcast-origin="home"
      title={`Broadcasting on ${brand.name}`}
      className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-faint"
    >
      <img
        src={brand.logoMarkMini}
        alt=""
        aria-hidden
        className={small ? 'h-3.5 w-3.5 rounded-[4px]' : 'h-4 w-4 rounded-[5px]'}
      />
      {brand.name}
    </span>
  );
}
